import { useEffect, useState } from 'react';
import { eventsApi } from '../../../api/eventsApi';
import { gridColumnCount } from '../../../hooks/useFittingPageSize';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { usePagedList } from '../../../hooks/usePagedList';
import { filterParams } from '../../../utils/listFilters';

const SEARCH_DEBOUNCE_MS = 300;

export const NO_EVENT_FILTERS = { status_filter: 'all', date_from: '', date_to: '' };

// Used for the very first request, before the grid is on screen.
// Mirrors .events-grid in events.css (grid-auto-rows, gap, widest column count).
const GRID_FALLBACK = { itemHeight: 170, gap: 12, columns: 3 };

const readGridLayout = (area) => {
  const grid = area.querySelector('.events-grid');
  if (!grid) return null;

  const styles = window.getComputedStyle(grid);
  return {
    itemHeight: parseFloat(styles.gridAutoRows) || GRID_FALLBACK.itemHeight,
    gap: parseFloat(styles.rowGap) || GRID_FALLBACK.gap,
    columns: gridColumnCount(grid) || GRID_FALLBACK.columns,
  };
};

const eventsOf = (response) => {
  if (Array.isArray(response?.events)) return response.events;
  if (Array.isArray(response?.results)) return response.results;
  return [];
};

const useEventStatusOptions = () => {
  const [statusOptions, setStatusOptions] = useState(['all']);

  useEffect(() => {
    let cancelled = false;
    eventsApi.getFacets()
      .then((facets) => {
        if (cancelled) return;
        const statuses = Array.isArray(facets?.statuses) ? facets.statuses : [];
        setStatusOptions(['all', ...statuses.map((s) => String(s).toUpperCase())]);
      })
      .catch((err) => console.error('Failed to load event facets:', err));
    return () => { cancelled = true; };
  }, []);

  return statusOptions;
};

/** Search, filters and sort for the events grid, and the page of events that fits the screen. */
export function useEventList() {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(NO_EVENT_FILTERS);
  const [sort, setSort] = useState('newest');
  const search = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const statusOptions = useEventStatusOptions();

  const list = usePagedList({
    listKey: 'events',
    queryKey: JSON.stringify([search, filters, sort]),
    fetchPage: async ({ page, perPage }) => {
      const { status_filter, date_from, date_to } = filterParams(filters);
      const response = await eventsApi.getAll({
        skip: (page - 1) * perPage,
        limit: perPage,
        status_filter,
        search: search || undefined,
        date_from,
        date_to,
        sort,
      });
      return { items: eventsOf(response), total: response?.total };
    },
    getLayout: readGridLayout,
    fallbackLayout: GRID_FALLBACK,
    errorMessage: 'Failed to load events. Please try again later.',
  });

  const setFilter = (name, value) => setFilters((current) => ({ ...current, [name]: value }));

  return { ...list, query, setQuery, filters, setFilter, setFilters, sort, setSort, statusOptions };
}
